import Link from 'next/link'
import type { Hit } from 'instantsearch.js'
import {
  isToday,
  isTomorrow,
  isThisWeek,
  isThisMonth,
  isYesterday,
  isSameWeek,
  subWeeks,
  isSameMonth,
  subMonths,
} from 'date-fns'

import { GridItemCountdownTimer } from '@/components/grid-archive/CountdownTimer'

import PostLabel from '@/components/ui/PostLabel'
import AppImage from '@/components/AppImage'
import { BookmarkAction, BookmarkButtonDashboard } from '@/components/post-actions/BookmarkAction'

import { decodeSafeEntities } from '@/lib/sanitise-strings'

export type GridArchiveItemProps = {
  hit: Hit
  size?: 'l'
  enableBookmark?: boolean
  className?: string
}

function getDateInWords(date: Date): string {
  if (isToday(date)) {
    return 'Today'
  }
  if (isTomorrow(date)) {
    return 'Tomorrow'
  }
  if (isThisWeek(date)) {
    return 'This Week'
  }
  if (isThisMonth(date)) {
    return 'This Month'
  }

  return ''
}

/**
 * --------------------------------------------------------------------------
 * Get Updated Label
 *
 * @param {Date|string|number} inputDate
 * @returns {string}
 * --------------------------------------------------------------------------
 **/
export function getUpdatedLabel(inputDate) {
  const date = new Date(inputDate)
  const now = new Date()

  if (isThisWeek(date)) {
    return 'Updated this week'
  }

  if (isThisMonth(date) || isSameMonth(date, subMonths(now, 1))) {
    return 'Recently updated'
  }

  // For dates older than last month, return nothing
  return null
}

export default function GridArchiveItem({
  hit,
  size,
  enableBookmark = false,
  className = '',
}: GridArchiveItemProps) {
  if (!hit.post_title || !hit.permalink) {
    return null
  }

  const countdownDate = hit?.countdown_date_unix
  const countdownLabel = hit?.countdown_label
  const isLive = countdownDate ? countdownDate > Date.now() / 1000 : false

  const imageUrl = hit?.images?.grid_image_url || null
  const imageAlt = hit?.images?.grid_image_alt || null

  const postId = parseInt(hit.objectID) || parseInt(hit.post_id) || null
  const postTitle = decodeSafeEntities(hit.post_title)
  const businessName = decodeSafeEntities(hit.business_info?.name)
  const location = decodeSafeEntities(hit?.location)

  const postDate = hit?.post_date_formatted

  const showLastModified = hit?.show_last_modified ?? false
  const lastModifiedDate = showLastModified ? hit?.last_modified_unix : null
  const lastModifiedInWords = lastModifiedDate
    ? getUpdatedLabel(new Date(lastModifiedDate * 1000))
    : null

  const eventDate = hit?.event_date?.date_formatted_long
  const eventDateLabel = hit?.event_date?.start_date_unix
    ? new Date(hit.event_date.start_date_unix * 1000)
    : null
  const eventDateInWords = eventDateLabel ? getDateInWords(eventDateLabel) : null

  let sizes = '100vw'

  if (size === 'l') {
    sizes = '(min-width: 1920px) 828px, (min-width: 500px) 43.57vw, calc(95.56vw - 19px)'
  } else {
    sizes =
      '(min-width: 1960px) 393px, (min-width: 1520px) calc(16.19vw + 79px), (min-width: 1300px) calc(21vw - 33px), (min-width: 1000px) 29.64vw, (min-width: 780px) calc(28vw - 26px), (min-width: 500px) calc(46.54vw - 15px), (min-width: 340px) 94.29vw, calc(1175vw - 3472px)'
  }

  return (
    <article
      className={`flex w-full flex-col justify-start gap-xs duration-300 @container/grid-item hover:translate-y-2 s:gap-xxs ${className}`}>
      <div className="group relative">
        {imageUrl ? (
          <Link
            href={hit.permalink}
            aria-label={`view article ${postTitle}`}
            className="relative mb-s flex flex-col overflow-hidden bg-primary-25 duration-500 group-hover:text-primary s:mb-xs">
            <GridItemCountdownTimer countdownDate={countdownDate} countdownLabel={countdownLabel} />

            <AppImage
              path={imageUrl}
              alt={imageAlt || ''}
              height={237}
              width={396}
              loading="lazy"
              transforms={{
                width: '396',
                height: '237',
                focus: 'auto',
                dpr: 'auto',
                quality: 'auto',
              }}
              className="w-full object-contain"
              lqip={{
                active: false,
              }}
              sizes={sizes}
              srcSet={{
                396: { width: '396', height: '237' },
                606: { width: '606', height: '363' },
                828: { width: '828', height: '496' },
              }}
            />

            {eventDateInWords ? (
              <PostLabel className="absolute left-0 top-0 z-10 m-xs s:m-xxs">
                {eventDateInWords}
              </PostLabel>
            ) : null}

            {lastModifiedInWords ? (
              <PostLabel className="absolute left-0 top-0 z-10 m-xs s:m-xxs">
                {lastModifiedInWords}
              </PostLabel>
            ) : null}
          </Link>
        ) : null}
        <Link
          href={hit.permalink}
          className="h5 xl:h6 transition-color mb-0 !line-clamp-3 text-ellipsis duration-200 ease-in-out group-hover:text-primary">
          {isLive ? <span className="font-serif text-primary">Live: </span> : null}
          {postTitle}
        </Link>

        {postId && enableBookmark ? (
          <div className="z-60 absolute right-0 top-0 m-8 bg-white">
            <BookmarkAction postId={postId}>
              <BookmarkButtonDashboard />
            </BookmarkAction>
          </div>
        ) : null}
      </div>

      {/* insights & news */}
      {hit.post_type === 'all-work' || hit.post_type === 'all-news' ? (
        <ul className="w-full leading-none text-grey-800 prose-li:leading-inherit">
          <li className="p--xsmall mr-xxxs inline">
            {businessName ? businessName : 'Jersey Finance'}
          </li>

          {postDate ? (
            <li className="p--xsmall inline !leading-tight">
              <span className="mr-xxxs">|</span>
              {postDate}
            </li>
          ) : null}
        </ul>
      ) : null}

      {/* events */}
      {hit.post_type === 'all-events' ? (
        <ul className="w-full leading-none text-grey-800 prose-li:leading-inherit">
          {eventDate ? <li className="p--xsmall mr-xxxs inline">{eventDate}</li> : null}

          {location ? (
            <li className="p--xsmall inline">
              {location && eventDate ? <span className="mr-xxxs">|</span> : null}
              {location}
            </li>
          ) : null}
        </ul>
      ) : null}
    </article>
  )
}
